/**
 * Layout root — shell app (Navbar/Header hoặc intro tối giản).
 *
 * getProfile rule: F5/hard load → sync profile 1 lần khi mount (ref chống refetch SPA).
 * Logo Navbar sync riêng khi vào /intro. Không getProfile khi chuyển trang.
 */
import React, { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { Spin } from 'antd';
import { Header } from '../Header';
import { Navbar } from '../Navbar';
import NoPermissionCard from '../common/NoPermissionCard';
import { getControllerFromRoute, isPageGuardExemptRoute } from '@/helper/RouteHelper';
import { resolveEffectivePathname } from '@/components/PhanHe/VanHoaPhanHeHandler';
import { isStandaloneDotOnTapDetailPath, normalizeHeaderPath } from '@/configs/ROUTE_HEADER';
import { hasControllerPageAccess, usePermission } from '@/hooks/usePermission';
import { useAuthStore } from '@/store/useAuthStore';
import { FloatingViecTrongNgayPanel } from './FloatingViecTrongNgayPanel';
import { FloatingCopyDataPanel } from './FloatingCopyDataPanel';
import { FloatingAiAssistant } from './FloatingAiAssistant';
import { OrgScopeProvider } from '@/contexts/OrgScopeContext';
import { DemoCrudClickGuard } from '../common/DemoCrudClickGuard';
import { syncLoggedInUserPermissionsAfterRoleGroupChange } from '@/utils/organizationIndexedDBUtils';

interface Props {
  children: React.ReactNode;
  isIntro?: boolean;
  profilePhanHeSyncing?: boolean;
}

export const Layout: React.FC<Props> = ({ children, isIntro, profilePhanHeSyncing = false }) => {
  const router = useRouter();
  const normalizedPathname = router.pathname.split('?')[0].split('#')[0].replace(/\/$/, '') || '/';
  const controllerPath = normalizedPathname === '/404'
    ? resolveEffectivePathname(router.pathname, router.asPath)
    : router.pathname;
  let controller = getControllerFromRoute(controllerPath);
  // Nếu controller là 'buoc11' thì dùng 'buoc6' để check permission
  if (controller === 'buoc11') controller = 'buoc6';
  const permissions = usePermission(controller);
  const profile = useAuthStore((state) => state.profile);
  const hydrated = useAuthStore((state) => state.hydrated);
  const isAuth = useAuthStore((state) => state.isAuth);
  const isAdmin = profile?.isAdmin;
  // Route đã map controller: bắt buộc có quyền trang.
  // Trước đây `permissions === null` vẫn cho vào → bypass khi thiếu role / sai map.
  const canAccessPage = Boolean(
    isAdmin || !controller || isPageGuardExemptRoute(controllerPath) || hasControllerPageAccess(permissions),
  );
  const didFetchProfileRef = useRef(false);

  // F5 / hard load: getProfile 1 lần khi Layout mount — không refetch khi SPA navigate
  useEffect(() => {
    if (!hydrated || !isAuth || !profile?.id) return;
    if (didFetchProfileRef.current) return;
    didFetchProfileRef.current = true;
    void syncLoggedInUserPermissionsAfterRoleGroupChange();
  }, [hydrated, isAuth, profile?.id]);

  const routePathForLayout = normalizeHeaderPath(router.asPath) || normalizeHeaderPath(router.pathname);
  const blockContentDuringPhanHeSync = !isStandaloneDotOnTapDetailPath(routePathForLayout);

  if (isIntro) {
    return (
      <div>
        {children}
      </div>
    );
  }

  return (
    <OrgScopeProvider>
      <div className="flex">
        <Navbar profilePhanHeSyncing={profilePhanHeSyncing} />

        <main className="pb-[15px] pl-[16px] pr-[16px] max-h-screen overflow-y-scroll w-full">
          {/* == Header Page == */}
          <Header />

          <div className="relative">
            <DemoCrudClickGuard />
            {/* == Page Content == */}
            {profilePhanHeSyncing && blockContentDuringPhanHeSync ? (
              <div className="flex items-center justify-center min-h-[320px]">
                <Spin size="large" />
              </div>
            ) : canAccessPage ? (
              <>{children}</>
            ) : (
              <NoPermissionCard />
            )}
          </div>
        </main>
        <FloatingViecTrongNgayPanel />
        <FloatingCopyDataPanel />
        <FloatingAiAssistant />
      </div>
    </OrgScopeProvider>
  );
};
